<template>
  <div
    class="appointment-report ar-srf"
    :class="{ 'ar-srf--compact': compact }"
    :aria-busy="isLoadingView || srIsFinalizing ? 'true' : 'false'"
  >
    <div class="ar-srf__body">
      <!-- Carregando -->
      <div
        v-if="isLoadingView"
        class="ar-srf__loading"
      >
        <svg
          v-if="compact"
          class="ar-spinner"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <circle
            cx="12"
            cy="12"
            r="9"
            stroke="currentColor"
            stroke-opacity=".25"
            stroke-width="2.5"
          />
          <path
            d="M21 12a9 9 0 0 0-9-9"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
          />
        </svg>
        <div
          v-else
          class="ar-questions ar-srf__skeleton"
          aria-hidden="true"
        >
          <div
            v-for="row in SKELETON_ROWS"
            :key="row"
            class="ar-srf__skeleton-row"
          >
            <span class="ar-srf__skeleton-legend" />
            <span class="ar-srf__skeleton-control" />
          </div>
        </div>
      </div>

      <!-- Falha ao carregar -->
      <section
        v-else-if="srStatus === SR_REPORT_STATUS.LOAD_ERROR"
        class="ar-srf__notice"
        :class="{ 'ar-card': !compact }"
      >
        <div
          class="ar-callout ar-callout--danger"
          role="alert"
        >
          <feather-icon
            icon="AlertTriangleIcon"
            size="20"
            aria-hidden="true"
          />
          <p class="ar-srf__notice-text">
            {{ srReport.errorMessage || APP_COPY.loadReportError }}
          </p>
        </div>
        <button
          type="button"
          class="ar-btn ar-btn--secondary ar-btn--sm"
          @click="srLoad()"
        >
          <feather-icon
            icon="RefreshCwIcon"
            size="16"
            aria-hidden="true"
          />
          {{ NEW_COPY.retry }}
        </button>
      </section>

      <!-- Sem formulário: não é o PN Responsável ou o compromisso saiu do funil (Momento X = o outro formulário) -->
      <section
        v-else-if="showUnavailable"
        class="ar-srf__notice"
        :class="{ 'ar-card': !compact }"
      >
        <p
          class="ar-callout ar-callout--warning"
          role="alert"
        >
          <feather-icon
            icon="AlertCircleIcon"
            size="16"
            aria-hidden="true"
          />
          <span>{{ srReport.errorMessage || APP_COPY.notReportable }}</span>
        </p>
        <!-- A página põe aqui o "voltar" (Compromissos / central), como no aviso do Momento X. -->
        <slot name="unavailable-actions" />
      </section>

      <!-- Salvo / já reportado (página). Na central, a confirmação é da própria central. -->
      <report-success
        v-else-if="srIsDone && !compact"
        :title="NEW_COPY.reportSaved"
        :report="srReport.report"
        :summary="srSummaryLines"
        :appointment="srReport.appointment"
        :customer-name="customerName"
        :reported-by="successReportedBy"
        :reported-at="successReportedAt"
        :notice="successNotice"
      >
        <template #next="scope">
          <!-- Próximo passo da sidebar: contrato da Parceria fechada / questionário da entrevista individual -->
          <section
            v-if="srPostSave"
            class="ar-card ar-srf__post"
            :aria-labelledby="ids.postSave"
          >
            <p
              :id="ids.postSave"
              class="ar-srf__post-question"
            >
              {{ postSaveCopy.question }}
            </p>
            <div class="ar-srf__post-actions">
              <button
                type="button"
                class="ar-btn ar-btn--primary"
                @click="onPostSavePrimary"
              >
                {{ postSaveCopy.now }}
              </button>
              <button
                type="button"
                class="ar-btn ar-btn--secondary"
                @click="srDismissPostSave"
              >
                {{ postSaveCopy.later }}
              </button>
            </div>
          </section>
          <p
            v-else-if="srContractDeferred"
            class="ar-callout ar-callout--info ar-srf__next-callout"
          >
            <feather-icon
              icon="FilePlusIcon"
              size="16"
              aria-hidden="true"
            />
            <span>{{ NEW_COPY.contractAfterPendencies }}</span>
          </p>

          <!-- O report foi salvo, mas o reagendamento falhou: tenta só o reagendamento de novo -->
          <div
            v-if="showRescheduleRetry"
            class="ar-callout ar-callout--warning ar-srf__next-callout ar-srf__reschedule-error"
            role="alert"
          >
            <feather-icon
              icon="AlertCircleIcon"
              size="16"
              aria-hidden="true"
            />
            <span class="ar-srf__reschedule-error-text">{{ srReport.reschedule.message }}</span>
            <button
              type="button"
              class="ar-btn ar-btn--secondary ar-btn--sm"
              :aria-busy="srReport.reschedule.status === 'saving' ? 'true' : 'false'"
              @click="srRetryReschedule"
            >
              {{ NEW_COPY.retry }}
            </button>
          </div>

          <slot
            name="next"
            v-bind="nextSlotProps(scope)"
          />
        </template>
      </report-success>

      <!-- Formulário (aguardando horário / preenchendo / finalizando / erro) -->
      <template v-else-if="showForm">
        <report-appointment-card
          v-if="appointmentCard && !compact"
          :appointment="srReport.appointment"
        />

        <report-date-lock-banner
          v-if="srShowDateLock"
          ref="dateLock"
          class="ar-srf__date-lock"
          v-bind="srDateLockProps"
          :hint="srIsEarly ? NEW_COPY.earlyNotHeldHint : ''"
          :show-actions="!compact"
          v-on="dateLockListeners"
        />

        <div
          class="ar-questions ar-srf__questions"
          :class="{ 'ar-questions--compact': compact }"
        >
          <div class="ar-classification">
            <report-reason-select
              :ref="srItemRef(F.REPORT_CLASSIFICATION)"
              v-bind="srClassificationProps()"
              v-on="srClassificationListeners()"
            />
            <p class="ar-classification__help">
              {{ REPORT_CLASSIFICATION_COPY.help }}
            </p>
          </div>

          <!-- Houve reunião? / Houve reunião com o PN? → (Não) Qual o motivo? -->
          <report-yes-no
            :ref="srItemRef(F.HAD_MEETING)"
            v-bind="srYesNoProps(F.HAD_MEETING)"
            v-on="srListeners(F.HAD_MEETING)"
          >
            <template #follow-up>
              <report-reason-select
                v-if="srFollowUpVisible(F.HAD_MEETING)"
                :ref="srItemRef(F.WHY)"
                v-bind="srWhyProps()"
                v-on="srListeners(F.WHY)"
              />
            </template>
          </report-yes-no>

          <!-- Fechou negócio? / Foi aprovado? → (Não) Qual o motivo? -->
          <report-yes-no
            v-if="srShowResult"
            :key="srResultKey"
            :ref="srItemRef(srResultKey)"
            v-bind="srYesNoProps(srResultKey)"
            v-on="srListeners(srResultKey)"
          >
            <template #follow-up>
              <report-reason-select
                v-if="srFollowUpVisible(srResultKey)"
                :ref="srItemRef(F.WHY)"
                v-bind="srWhyProps()"
                v-on="srListeners(F.WHY)"
              />
            </template>
          </report-yes-no>

          <!-- Deseja remarcar a reunião? → (Sim) datas, formato e link/endereço do novo compromisso -->
          <report-yes-no
            v-if="srShowRescheduleQuestion"
            :ref="srItemRef(F.RESCHEDULE)"
            v-bind="srYesNoProps(F.RESCHEDULE)"
            v-on="srListeners(F.RESCHEDULE)"
          >
            <template #follow-up>
              <div
                v-if="srFollowUpVisible(F.RESCHEDULE)"
                class="ar-srf__reschedule"
              >
                <div class="ar-srf__field">
                  <label
                    :for="ids.start"
                    class="ar-srf__label"
                  >{{ STANDARD_COPY.meetingStart }}</label>
                  <flat-pickr
                    :id="ids.start"
                    :ref="srItemRef(F.MEETING_START)"
                    :value="srReport.answers[F.MEETING_START]"
                    :config="pickerConfig"
                    class="ar-srf__input ar-tabular"
                    :class="{ 'ar-srf__input--invalid': srShowItemError(F.MEETING_START) }"
                    :disabled="!srIsEditable"
                    :aria-invalid="srShowItemError(F.MEETING_START) ? 'true' : null"
                    :aria-describedby="srShowItemError(F.MEETING_START) ? errorId(F.MEETING_START) : null"
                    @input="value => srSetAnswer(F.MEETING_START, value || null)"
                  />
                  <span
                    v-if="srShowItemError(F.MEETING_START)"
                    :id="errorId(F.MEETING_START)"
                    class="ar-srf__error ar-anim-fade-in"
                  >{{ srItemMessage(F.MEETING_START) }}</span>
                </div>

                <div class="ar-srf__field">
                  <label
                    :for="ids.end"
                    class="ar-srf__label"
                  >{{ STANDARD_COPY.meetingEnd }}</label>
                  <flat-pickr
                    :id="ids.end"
                    :ref="srItemRef(F.MEETING_END)"
                    :value="srReport.answers[F.MEETING_END]"
                    :config="pickerConfig"
                    class="ar-srf__input ar-tabular"
                    :class="{ 'ar-srf__input--invalid': srShowItemError(F.MEETING_END) }"
                    :disabled="!srIsEditable"
                    :aria-invalid="srShowItemError(F.MEETING_END) ? 'true' : null"
                    :aria-describedby="srShowItemError(F.MEETING_END) ? errorId(F.MEETING_END) : null"
                    @input="value => srSetAnswer(F.MEETING_END, value || null)"
                  />
                  <span
                    v-if="srShowItemError(F.MEETING_END)"
                    :id="errorId(F.MEETING_END)"
                    class="ar-srf__error ar-anim-fade-in"
                  >{{ srItemMessage(F.MEETING_END) }}</span>
                </div>

                <report-reason-select
                  :ref="srItemRef(F.MEETING_FORMAT)"
                  class="ar-srf__span"
                  v-bind="srMeetingFormatProps()"
                  v-on="srListeners(F.MEETING_FORMAT)"
                />

                <div class="ar-srf__field ar-srf__span">
                  <label
                    :for="ids.roomLink"
                    class="ar-srf__label"
                  >{{ srRoomLinkLabel }}</label>
                  <input
                    :id="ids.roomLink"
                    :ref="srItemRef(F.ROOM_LINK)"
                    type="text"
                    class="ar-srf__input"
                    :class="{ 'ar-srf__input--invalid': srShowItemError(F.ROOM_LINK) }"
                    :value="srReport.answers[F.ROOM_LINK] || ''"
                    :placeholder="srInPerson ? '' : FIGMA_COPY.meetingLinkPlaceholder"
                    :inputmode="srInPerson ? null : 'url'"
                    autocomplete="off"
                    :readonly="!srIsEditable"
                    :aria-invalid="srShowItemError(F.ROOM_LINK) ? 'true' : null"
                    :aria-describedby="srShowItemError(F.ROOM_LINK) ? errorId(F.ROOM_LINK) : null"
                    @input="srSetAnswer(F.ROOM_LINK, $event.target.value || null)"
                    @blur="srTouch(F.ROOM_LINK)"
                  >
                  <span
                    v-if="srShowItemError(F.ROOM_LINK)"
                    :id="errorId(F.ROOM_LINK)"
                    class="ar-srf__error ar-anim-fade-in"
                  >{{ srItemMessage(F.ROOM_LINK) }}</span>
                </div>
              </div>
            </template>
          </report-yes-no>

          <template v-if="!srHeldLocked">
            <!-- Comentários do report (opcional) -->
            <div class="ar-srf__row ar-srf__field">
              <label
                :for="ids.feedback"
                class="ar-srf__label"
              >{{ STANDARD_COPY.feedback }}</label>
              <textarea
                :id="ids.feedback"
                class="ar-srf__input ar-srf__textarea"
                :class="{ 'ar-srf__input--invalid': !!srReport.serverErrors[F.FEEDBACK] }"
                rows="3"
                :value="srReport.answers[F.FEEDBACK] || ''"
                :readonly="!srIsEditable"
                :aria-invalid="srReport.serverErrors[F.FEEDBACK] ? 'true' : null"
                :aria-describedby="srReport.serverErrors[F.FEEDBACK] ? errorId(F.FEEDBACK) : null"
                @input="srSetAnswer(F.FEEDBACK, $event.target.value || null)"
              />
              <span
                v-if="srReport.serverErrors[F.FEEDBACK]"
                :id="errorId(F.FEEDBACK)"
                class="ar-srf__error ar-anim-fade-in"
              >{{ srReport.serverErrors[F.FEEDBACK] }}</span>
            </div>

            <!-- Avalie o PN Especialista -->
            <template v-if="srShowSpecialistBlock">
              <div class="ar-srf__row ar-srf__section">
                <component
                  :is="compact ? 'h4' : 'h3'"
                  class="ar-srf__section-title"
                >
                  {{ STANDARD_COPY.specialistSection }}
                </component>
                <p class="ar-srf__section-info">
                  <feather-icon
                    icon="InfoIcon"
                    size="16"
                    class="ar-srf__section-icon"
                    aria-hidden="true"
                  />
                  <span>{{ STANDARD_COPY.specialistInfo }}</span>
                </p>
              </div>

              <!-- Parceria / Negociação: "Houve um especialista suporte…?" → (Sim) especialista + avaliação -->
              <report-yes-no
                v-if="srContext.asksSupport"
                :ref="srItemRef(F.HAD_SUPPORT)"
                v-bind="srYesNoProps(F.HAD_SUPPORT)"
                v-on="srListeners(F.HAD_SUPPORT)"
              >
                <template #follow-up>
                  <div
                    v-if="srFollowUpVisible(F.HAD_SUPPORT)"
                    class="ar-srf__specialist"
                  >
                    <report-reason-select
                      :ref="srItemRef(F.SPECIALIST_ID)"
                      v-bind="srSpecialistProps()"
                      v-on="srListeners(F.SPECIALIST_ID)"
                    />
                    <report-specialist-rating
                      v-if="srShowRating"
                      :ref="srItemRef(F.SPECIALIST_SCORE)"
                      v-bind="srRatingProps()"
                      v-on="srRatingListeners()"
                    />
                  </div>
                </template>
              </report-yes-no>

              <!-- Entrevista: o especialista do compromisso já vem escolhido; a avaliação é opcional -->
              <div
                v-else
                class="ar-srf__row ar-srf__specialist"
              >
                <report-reason-select
                  :ref="srItemRef(F.SPECIALIST_ID)"
                  v-bind="srSpecialistProps()"
                  v-on="srListeners(F.SPECIALIST_ID)"
                />
                <report-specialist-rating
                  v-if="srShowRating"
                  :ref="srItemRef(F.SPECIALIST_SCORE)"
                  v-bind="srRatingProps()"
                  v-on="srRatingListeners()"
                />
              </div>
            </template>
          </template>

          <!-- Antes do horário, sem "Não": a prévia inclui a pergunta do especialista (sem controles) -->
          <report-yes-no
            v-else-if="srContext.asksSupport"
            v-bind="srYesNoProps(F.HAD_SUPPORT)"
          />
        </div>
      </template>
    </div>

    <!-- Barra: status + autosave + "Finalizar report" (erro acima dela, role="alert") -->
    <report-action-bar
      v-if="srShowActionBar"
      :class="barClass"
      v-bind="srActionBarProps"
      @finalize="srFinalize"
      @jump-to-missing="srJumpToMissing()"
      @locked="showDateLock"
    />

    <span
      class="ar-sr-only"
      aria-live="polite"
    >{{ srReport.announce }}</span>
  </div>
</template>

<script>
import flatPickr from 'vue-flatpickr-component'
// flatpickr vem como dependência do vue-flatpickr-component (o mesmo import da sidebar e do Novo compromisso).
// eslint-disable-next-line import/no-extraneous-dependencies
import { Portuguese } from 'flatpickr/dist/l10n/pt'
import * as accountTypes from '@/modules/account/store/types'
import { dateToLocalYMD } from '@/helpers/converters'
import {
  APP_COPY,
  FIGMA_COPY,
  NEW_COPY,
  REPORT_CLASSIFICATION_COPY,
  REPORT_ERROR_CODES,
} from '@/constants/appointment_report_v2'
import { STANDARD_COPY, STANDARD_FIELDS } from '@/constants/appointment_report_standard'
import standardReportForm, { SR_POST_SAVE, SR_REPORT_STATUS } from '../mixins/standardReportForm'
import { prefersReducedMotion } from '../helpers/report_ui'
// ReportSuccess antes dos demais, como nas páginas do Report e da central: o CSS dos componentes compartilhados sai
// na mesma ordem em todos os chunks (senão o mini-css-extract acusa "Conflicting order").
import ReportSuccess from './ReportSuccess.vue'
import ReportActionBar from './ReportActionBar.vue'
import ReportAppointmentCard from './ReportAppointmentCard.vue'
import ReportDateLockBanner from './ReportDateLockBanner.vue'
import ReportReasonSelect from './ReportReasonSelect.vue'
import ReportYesNo from './ReportYesNo.vue'
import ReportSpecialistRating from './ReportSpecialistRating.vue'

const SKELETON_ROWS = [1, 2, 3]

const FORM_STATUSES = [
  SR_REPORT_STATUS.WAITING,
  SR_REPORT_STATUS.EDITING,
  SR_REPORT_STATUS.FINALIZING,
  SR_REPORT_STATUS.ERROR,
]

// "2026-09-30 14:07" no relógio LOCAL (sem toISOString/UTC) — formato que os helpers de data do report leem.
const localWallClock = date => `${dateToLocalYMD(date)} ${String(date.getHours()).padStart(2, '0')}:`
  + `${String(date.getMinutes()).padStart(2, '0')}`

/**
 * Report dos tipos padrão — Entrevista, Parceria, Negociação e recrutamento de PN — no visual novo do Report
 * (boards v4-report / v4-pendencias), com as perguntas, opções, textos, condições e próximos passos de SEMPRE (os da
 * antiga AppointmentReportSidebar) e as proteções do Momento X no servidor (dono, status, horário, idempotência, matriz
 * de obrigatórios). Estado e regras: mixin `standardReportForm`; o Momento X continua no `momentXReportForm`.
 *
 * A tela escolhe o formulário pelo tipo do compromisso (Momento X → o do CX; demais → este). Não está em
 * `components/index.js` de propósito: cada página importa direto (o flatpickr só entra onde é usado).
 *
 *   <standard-report-form :appointment-id="id" appointment-card bar-class="…" @reported="…" @already-reported="…">
 *     <template #next="{ closedDeal, pendingCount, toggleDetails, … }"> próximos passos da tela </template>
 *     <template #unavailable-actions> "voltar" do aviso sem formulário </template>
 *   </standard-report-form>
 *
 * Props: `appointmentId`; `appointment` = o que a tela já tem do compromisso (opcional; só o `specialist` do GET do
 * compromisso é aproveitado); `preloaded` = a resposta do GET do rascunho que a tela já buscou para este compromisso
 * (opcional; lida só ao montar — ver `srInit`); `compact` = painel da central (linhas baixas, barra `pane`,
 * "Finalizando…" ≥ 700ms, e a confirmação fica com a central); `appointmentCard` = mostra o cartão do compromisso
 * acima das perguntas (página); `barClass` = classe da barra fixa (a página a estende até as bordas da coluna).
 *
 * Eventos: os do mixin (`loaded`, `reported`, `already-reported`, `pending-rejected`, `draft-saved`, `rescheduled`,
 * `post-save-dismissed`), `status-change` (cada troca de `SR_REPORT_STATUS`, inclusive a inicial — a página liga o
 * stepper, a barra do <body> e o espaçamento do "salvo" nele) e `edit-appointment` ("Editar Compromisso" do
 * aguardando horário — só aparece se a tela ouvir o evento).
 */
export default {
  name: 'StandardReportForm',
  components: {
    flatPickr,
    ReportActionBar,
    ReportAppointmentCard,
    ReportDateLockBanner,
    ReportReasonSelect,
    ReportSpecialistRating,
    ReportSuccess,
    ReportYesNo,
  },
  mixins: [standardReportForm],
  props: {
    appointmentId: {
      type: [Number, String],
      required: true,
    },
    appointment: {
      type: Object,
      default: null,
    },
    preloaded: {
      type: Object,
      default: null,
    },
    compact: {
      type: Boolean,
      default: false,
    },
    appointmentCard: {
      type: Boolean,
      default: false,
    },
    barClass: {
      type: [String, Array, Object],
      default: null,
    },
  },
  data() {
    return {
      APP_COPY,
      FIGMA_COPY,
      NEW_COPY,
      REPORT_CLASSIFICATION_COPY,
      STANDARD_COPY,
      SR_REPORT_STATUS,
      SKELETON_ROWS,
      F: STANDARD_FIELDS,
      // Layout do painel da central (ver o mixin).
      srCompact: this.compact,
      // Mesma configuração do flatpickr da sidebar ("d/m/Y H:i", hora, pt-BR, a partir de hoje).
      pickerConfig: {
        dateFormat: 'd/m/Y H:i',
        enableTime: true,
        locale: Portuguese,
        minDate: 'today',
      },
      // Horário local em que o finalize respondeu (quando o resumo do servidor não traz `created_at`).
      savedLocalAt: '',
    }
  },
  computed: {
    isLoadingView() {
      return this.srIsLoading || this.srStatus === SR_REPORT_STATUS.IDLE
    },
    // Só nos estados do formulário — "indisponível" por ser Momento X (sem aviso, ver abaixo) não pode cair aqui.
    showForm() {
      return FORM_STATUSES.includes(this.srStatus)
    },
    // Momento X aberto aqui por engano: nada a mostrar (a tela troca para o formulário do CX pelo evento `loaded`).
    showUnavailable() {
      return this.srStatus === SR_REPORT_STATUS.UNAVAILABLE
        && this.srReport.unavailableCode !== REPORT_ERROR_CODES.WRONG_TYPE
    },
    ids() {
      const base = this.srUid
      return {
        start: `${base}-meeting-start`,
        end: `${base}-meeting-end`,
        roomLink: `${base}-room-link`,
        feedback: `${base}-feedback`,
        postSave: `${base}-post-save`,
      }
    },
    customerName() {
      const { appointment } = this.srReport
      return appointment && appointment.customer ? String(appointment.customer.name || '') : ''
    },
    currentUser() {
      return this.$store.getters[accountTypes.USER]
    },
    // "Finalizado por … em … às …": vem do resumo do servidor. Sem ele (resposta sem `reported_by`/`created_at`),
    // quem finalizou nesta tela é o usuário logado, no horário local da resposta — como no Momento X.
    successReportedBy() {
      const { report } = this.srReport
      if (!this.srIsSaved || (report && report.reported_by && report.reported_by.name)) return ''
      return this.currentUser && this.currentUser.name ? String(this.currentUser.name) : ''
    },
    successReportedAt() {
      const { report } = this.srReport
      if (!this.srIsSaved || (report && report.created_at)) return ''
      return this.savedLocalAt
    },
    successNotice() {
      return this.srStatus === SR_REPORT_STATUS.ALREADY_REPORTED ? APP_COPY.alreadyReported : ''
    },
    postSaveCopy() {
      if (this.srPostSave === SR_POST_SAVE.INDIVIDUAL_INTERVIEW) {
        return {
          question: STANDARD_COPY.individualInterviewQuestion,
          now: STANDARD_COPY.answerNow,
          later: STANDARD_COPY.answerLater,
        }
      }
      return {
        question: APP_COPY.contractReportQuestion,
        now: APP_COPY.reportNow,
        later: APP_COPY.reportLater,
      }
    },
    showRescheduleRetry() {
      return this.srIsSaved && ['error', 'saving'].includes(this.srReport.reschedule.status)
        && !!this.srReport.reschedule.message
    },
    dateLockListeners() {
      const listeners = { released: this.srOnReleased }
      if (this.$listeners && this.$listeners['edit-appointment']) listeners.edit = () => this.$emit('edit-appointment')
      return listeners
    },
  },
  watch: {
    appointmentId(id) {
      this.savedLocalAt = ''
      this.srInit(id, { appointment: this.appointment })
    },
    compact(value) {
      this.srCompact = value
    },
    // A tela que usa o formulário não lê o estado dele por `ref` (não é reativo): recebe cada troca por evento.
    srStatus: {
      immediate: true,
      handler(status) {
        this.$emit('status-change', status)
      },
    },
  },
  created() {
    this.srInit(this.appointmentId, { appointment: this.appointment, preloaded: this.preloaded })
  },
  methods: {
    // Gancho do mixin: report gravado por este formulário.
    onStandardReportFinalized() {
      this.savedLocalAt = localWallClock(new Date())
    },

    onPostSavePrimary() {
      if (this.srPostSave === SR_POST_SAVE.INDIVIDUAL_INTERVIEW) {
        this.srOpenIndividualInterview()
        return
      }
      this.srOpenContract()
    },

    errorId(key) {
      return `${this.srUid}-${key}-error`
    },

    // CTA travado ("Houve reunião? Sim" antes do horário): leva ao aviso de horário, que diz quando libera.
    showDateLock() {
      const banner = this.$refs.dateLock
      const element = banner && banner.$el
      if (element && typeof element.scrollIntoView === 'function') {
        element.scrollIntoView({ block: 'center', behavior: prefersReducedMotion() ? 'auto' : 'smooth' })
      }
    },

    // Escopo do slot `next` da tela: o do ReportSuccess ("Detalhes do report") + o resultado deste report.
    nextSlotProps(scope) {
      return {
        ...(scope || {}),
        report: this.srReport.report,
        alreadyReported: this.srStatus === SR_REPORT_STATUS.ALREADY_REPORTED,
        pendingCount: this.srReport.pendingCount,
        closedDeal: this.srClosedDeal,
        hasApproval: this.srHasApproval,
        postSave: this.srPostSave,
        contractDeferred: this.srContractDeferred,
      }
    },
  },
}
</script>

<style lang="scss" src="@/assets/scss/_appointment-report.scss"></style>

<style lang="scss" scoped>
.ar-srf {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.ar-srf__body {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

// ---- Painel da central: o corpo ocupa o painel e a barra é o rodapé dele ----
.ar-srf--compact {
  flex: 1 1 auto;
  min-height: 0;
  gap: 0;

  .ar-srf__body {
    flex: 1 1 auto;
    min-height: 0;
    gap: 0;
  }

  .ar-srf__loading {
    flex: 1 1 auto;
    min-height: 240px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--ar-text-muted);
  }

  .ar-srf__notice {
    padding: 20px 24px;
  }

  .ar-srf__date-lock {
    margin: 16px 24px 0;
  }

  // O painel não recorta o conteúdo (a lista do select pode passar da borda): os cantos de baixo do rodapé
  // acompanham o raio do card.
  ::v-deep .ar-action-bar--pane .ar-action-bar__bar {
    border-radius: 0 0 var(--ar-radius-card) var(--ar-radius-card);
  }
}

// ---- Carregando (sem animação em loop) ----
.ar-srf__skeleton-row {
  min-height: 72px;
  padding: 16px 0;
  display: flex;
  align-items: center;
  gap: 16px;
}

.ar-srf__skeleton-row + .ar-srf__skeleton-row {
  border-top: 1px solid var(--ar-divider);
}

.ar-srf__skeleton-legend {
  flex: 0 1 280px;
  height: 14px;
  border-radius: var(--ar-radius-thumb);
  background: var(--ar-bg-raised);
}

.ar-srf__skeleton-control {
  width: 200px;
  height: 40px;
  margin-left: auto;
  flex-shrink: 0;
  border-radius: var(--ar-radius-control);
  border: 1px solid var(--ar-divider);
  background: var(--ar-bg-field);
}

// ---- Indisponível / falha ao carregar ----
.ar-srf__notice {
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
}

.ar-srf__notice-text {
  margin: 0;
}

// ---- Linhas próprias do cartão (comentários, cabeçalho do especialista): divisória como a das perguntas ----
.ar-srf__row {
  padding: 16px 0;
  border-top: 1px solid var(--ar-divider);
}

.ar-srf__questions > .ar-srf__row:first-child {
  border-top: 0;
}

.ar-srf__row + .ar-question {
  border-top: 1px solid var(--ar-divider);
}

// ---- Campos (rótulo em cima, controle de 44px como o select, mensagem embaixo) ----
.ar-srf__field {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ar-srf__label {
  align-self: flex-start;
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  font-weight: 600;
  color: var(--ar-text-1);
}

.ar-srf__input {
  width: 100%;
  height: 44px;
  margin: 0;
  padding: 0 14px;
  display: block;
  border: 1px solid var(--ar-control);
  border-radius: var(--ar-radius-control);
  background: var(--ar-bg-field);
  box-shadow: none;
  font-family: inherit;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  color: var(--ar-text-1);
  transition:
    border-color var(--ar-dur-hover) var(--ar-ease-standard),
    background-color var(--ar-dur-hover) var(--ar-ease-standard);

  &::placeholder {
    font-weight: 400;
    color: var(--ar-text-placeholder);
    opacity: 1;
  }

  &:hover:not(:disabled):not([readonly]):not(.ar-srf__input--invalid) {
    border-color: var(--ar-control-hover);
    background: var(--ar-bg-field-hover);
  }

  &:focus {
    outline: none;
    border-color: var(--ar-brand-text);
  }

  &:focus-visible {
    outline: 2px solid var(--ar-focus);
    outline-offset: 1px;
  }

  &:disabled {
    cursor: default;
  }
}

.ar-srf__input--invalid,
.ar-srf__input--invalid:focus {
  border-color: var(--ar-warning);
}

.ar-srf__textarea {
  height: auto;
  min-height: 88px;
  padding: 10px 14px;
  resize: vertical;
  font-weight: 400;
}

.ar-srf__error {
  font-size: 12px;
  line-height: 16px;
  font-weight: 500;
  color: var(--ar-warning);
}

// ---- Reagendamento: início | término, formato e link/endereço em largura total ----
.ar-srf__reschedule {
  max-width: 600px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px 20px;
}

.ar-srf__span {
  grid-column: 1 / -1;
}

// ---- Avaliação do especialista ----
.ar-srf__section {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.ar-srf__section-title {
  margin: 0;
  font-size: 15px;
  line-height: 22px;
  font-weight: 600;
  color: var(--ar-text-1);
}

.ar-srf__section-info {
  margin: 0;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13px;
  line-height: 20px;
  font-weight: 400;
  color: var(--ar-text-2);
}

.ar-srf__section-icon {
  flex-shrink: 0;
  margin-top: 2px;
  color: var(--ar-brand-text);
}

.ar-srf__specialist {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

// ---- Depois de salvar ----
.ar-srf__post {
  width: 100%;
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  text-align: center;
}

.ar-srf__post-question {
  margin: 0;
  font-size: 15px;
  line-height: 22px;
  font-weight: 600;
  color: var(--ar-text-1);
}

.ar-srf__post-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
}

.ar-srf__next-callout {
  width: 100%;
}

.ar-srf__reschedule-error {
  align-items: center;
}

.ar-srf__reschedule-error-text {
  flex: 1 1 auto;
  min-width: 0;
}

@media (max-width: 575.98px) {
  .ar-srf__reschedule {
    grid-template-columns: minmax(0, 1fr);
  }

  .ar-srf__skeleton-row {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .ar-srf__skeleton-control {
    width: 100%;
    height: 48px;
    margin-left: 0;
  }

  .ar-srf__post-actions {
    width: 100%;
    flex-direction: column;

    .ar-btn {
      width: 100%;
      height: 48px;
    }
  }

  .ar-srf__reschedule-error {
    flex-wrap: wrap;
  }
}
</style>

<style lang="scss">
// Calendário do flatpickr (tema do Vuexy) — o mesmo import da sidebar e do Novo compromisso.
@import "@core/scss/vue/libs/vue-flatpicker.scss";
</style>
